import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Container, 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  useScrollTrigger, 
  Slide,
  Fab,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendlyBooking from './CalendlyBooking';
import { Link } from 'react-router-dom';
import AuthContext from '../store/AuthContext';
import { isLocalEnvironment } from '../utils/environmentUtils';

// Import sections
import HeroSection from '../sections/HeroSection';
import ServicesSection from '../sections/ServicesSection';
import ProjectsSection from '../sections/ProjectsSection';
import AboutSection from '../sections/AboutSection';
import ContactSection from '../sections/ContactSection';
import Footer from '../components/Footer';

// Hide navbar on scroll down, show on scroll up
function HideOnScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

// Scroll to top button
function ScrollTop() {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <Slide in={trigger} direction="up">
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 2,
        }}
      >
        <Fab
          color="primary"
          size="small"
          aria-label="scroll back to top"
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Box>
    </Slide>
  );
}

const SinglePageLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAuthenticated, isLocalEnv } = useContext(AuthContext);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      setMobileOpen(false);
    }
  };

  const navItems = [
    { name: 'Home', section: 'hero' },
    { name: 'Services', section: 'services' },
    { name: 'Projects', section: 'projects' },
    { name: 'About', section: 'about' },
    { name: 'Contact', section: 'contact' }
  ];

  const drawerWidth = 240;
  
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2 }}>
        <Box
          component="img"
          src="/images/rakdigitalDev1.png"
          alt="Rak Digital Developments Logo"
          sx={{
            height: '32px',
            marginRight: '10px',
            verticalAlign: 'middle'
          }}
        />
        Rak Digital Developments
      </Typography>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton sx={{ textAlign: 'center' }} onClick={() => scrollToSection(item.section)}>
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
        {isAuthenticated && isLocalEnv && (
          <>
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding>
              <ListItemButton 
                component={Link} 
                to="/project-manager"
                sx={{ 
                  textAlign: 'center',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(82, 113, 255, 0.08)',
                  }
                }}
              >
                <SettingsIcon sx={{ mr: 1 }} />
                <ListItemText primary="Project Manager" />
              </ListItemButton>
            </ListItem>
          </>
        )}
        {!isAuthenticated && isLocalEnv && (
          <ListItem disablePadding>
            <ListItemButton 
              component={Link}
              to="/admin-login"
              sx={{ 
                textAlign: 'center',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(82, 113, 255, 0.08)',
                }
              }}
            >
              <ListItemText primary="Admin Login" />
            </ListItemButton>
          </ListItem>
        )}
        <ListItem disablePadding>
          <ListItemButton 
            sx={{ 
              textAlign: 'center',
              bgcolor: 'primary.main',
              color: 'white',
              mx: 2,
              borderRadius: 1,
              mt: 1,
              '&:hover': {
                bgcolor: 'primary.dark',
              }
            }}
          >
            <CalendlyBooking 
              buttonText="Book a Call"
              buttonProps={{
                fullWidth: true,
                variant: 'contained',
                color: 'primary',
                sx: {
                  boxShadow: 'none'
                }
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <HideOnScroll>
        <AppBar 
          position="fixed" 
          elevation={0} 
          sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.9)', 
            backdropFilter: 'blur(8px)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
          }}
        >
          <Container maxWidth="lg">
            <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 2 } }}>
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Box
                  component="img"
                  src="/images/rakdigitalDev1.png"
                  alt="Rak Digital Developments Logo"
                  sx={{
                    height: '32px',
                    marginRight: '10px'
                  }}
                />
                Rak Digital Developments
              </Typography>
              
              {isMobile ? (
                <IconButton
                  color="primary"
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon />
                </IconButton>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {navItems.map((item) => (
                    <Button 
                      key={item.name}
                      onClick={() => scrollToSection(item.section)}
                      sx={{ 
                        color: 'text.primary',
                        '&:hover': {
                          color: 'primary.main',
                          backgroundColor: 'transparent'
                        }
                      }}
                    >
                      {item.name}
                    </Button>
                  ))}
                  {isAuthenticated && isLocalEnv && (
                    <Button
                      component={Link}
                      to="/project-manager"
                      startIcon={<SettingsIcon />}
                      sx={{ 
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'rgba(82, 113, 255, 0.08)',
                        }
                      }}
                    >
                      Project Manager
                    </Button>
                  )}
                  {!isAuthenticated && isLocalEnv && (
                    <Button
                      component={Link}
                      to="/admin-login"
                      sx={{ 
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'rgba(82, 113, 255, 0.08)',
                        }
                      }}
                    >
                      Admin Login
                    </Button>
                  )}
                  <Button 
                    variant="outlined"
                    color="primary"
                    onClick={() => scrollToSection('contact')}
                    sx={{ mr: 1 }}
                  >
                    Get Started
                  </Button>
                  <CalendlyBooking 
                    buttonText="Book a Call"
                    buttonProps={{
                      variant: 'contained',
                      color: 'primary'
                    }}
                  />
                </Box>
              )}
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>
      
      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          anchor="right"
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Toolbar /> {/* Spacer for fixed AppBar */}
        <Box id="hero">
          <HeroSection />
        </Box>
        <Box id="services">
          <ServicesSection />
        </Box>
        <Box id="projects">
          <ProjectsSection />
        </Box>
        <Box id="about">
          <AboutSection />
        </Box>
        <Box id="contact">
          <ContactSection />
        </Box>
      </Box>

      <Footer />
      <ScrollTop />
    </Box>
  );
};

export default SinglePageLayout; 